import { ReactNode, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { Document as PDFDocument, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Loader } from '../loader';
import { Resource } from '../../../../../interfaces';
import { LoadingPDFPage, LoadingPageLabel } from './loading-page';

// pdf.js 5 decodes JPEG 2000 images (common in InDesign-exported brochures)
// through its OpenJPEG WASM module, fetched at render time from `wasmUrl` —
// without it those images are silently skipped: a page renders its text but
// no pictures. cMaps and standard fonts load the same way for some documents.
// Served from unpkg because the cdnjs build (which hosts our worker — see the
// webapp AppProvider) does not ship the wasm/ directory. Module-level
// constant on purpose: a fresh object identity each render would make
// react-pdf reload the document.
const PDF_OPTIONS = {
  wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`
};

const useStyles = makeStyles({
  // react-pdf puts an inline white background on the page div; a rendered
  // page covers it with its canvas, but while a page is (re)loading the
  // white shows as a box around the "Loading page..." label on the grey
  // preview surfaces. Only the loading state (a message child, no canvas)
  // goes transparent — `!important` from a stylesheet beats an inline style.
  document: {
    '& .react-pdf__Page:has(> .react-pdf__message)': {
      backgroundColor: 'transparent !important'
    }
  }
});

interface DocumentProps {
  document: Resource;
  loading: boolean;
  activePage: number;
  /** Overrides the default "Loading PDF..." UI shown while the PDF loads. */
  loadingComponent?: ReactNode;
  /** Renders the page at this pixel width (zoom); omit for the natural size. */
  pageWidth?: number;
  /** Reports the document's page count once the PDF loads. */
  onLoadPages?: (numPages: number) => void;
}

export const Document = ({
  document,
  loading,
  activePage = 1,
  loadingComponent,
  pageWidth,
  onLoadPages
}: DocumentProps) => {
  const classes = useStyles();
  // Page count of the loaded PDF (0 until it loads). The rendered page is
  // fully controlled — derived from `activePage` below — so there is no page
  // state to fall out of sync with the prop across async loads.
  const [pageCount, setPageCount] = useState(0);
  const [loadingInfo, setLoadingInfo] = useState({
    loaded: 0,
    total: 0
  });

  // A new document is loading — clear the previous one's page count and
  // progress so they don't bleed into the new load.
  useEffect(() => {
    setPageCount(0);
    setLoadingInfo({ loaded: 0, total: 0 });
  }, [document.DownloadURLWithSas]);

  if (loading) return <Loader />;

  const pdfLoading = loadingComponent ?? (
    <LoadingPDFPage loaded={loadingInfo.loaded} total={loadingInfo.total} />
  );

  // Requested page, clamped to the document once its page count is known.
  const pageNumber = pageCount
    ? Math.min(Math.max(1, activePage), pageCount)
    : Math.max(1, activePage);

  return (
    <PDFDocument
      file={document.DownloadURLWithSas}
      className={classes.document}
      options={PDF_OPTIONS}
      loading={pdfLoading}
      onLoadProgress={info =>
        setLoadingInfo({ loaded: info.loaded, total: info.total })
      }
      onLoadSuccess={pdf => {
        setPageCount(pdf.numPages);
        onLoadPages?.(pdf.numPages);
      }}>
      <Page
        pageNumber={pageNumber}
        width={pageWidth}
        loading={loadingComponent ?? <LoadingPageLabel />}
      />
    </PDFDocument>
  );
};
